import { QueryBuilderModel } from 'bones/components/molecules/QueryBuilder/types/model';
import { QueryBuilderCopy } from 'bones/components/molecules/QueryBuilder/types/schema';
import {
  RDF_TYPE_IRI,
  XSD_BOOLEAN_IRI,
  XSD_DATE_IRI,
  XSD_DATE_TIME_IRI,
  XSD_DECIMAL_IRI,
  XSD_DOUBLE_IRI,
  XSD_FLOAT_IRI,
  XSD_INTEGER_IRI,
  XSD_TIME_IRI,
} from 'bones/constants/iris';
import { isValidDate } from 'bones/utils/dates/isValidDate';
import { getDateFromXsdTimeString } from 'bones/utils/dates/xsdFormat';
import {
  ANY_CLASS_IRI,
  DATE_SPLIT_SYMBOL,
  DatatypeOperatorType,
  Direction,
  OperatorType,
} from 'bones/utils/queryBuilder/constants';
import { getUsedVariables } from 'bones/utils/queryBuilder/helpers/getUsedVariables';
import { sliceTopExpression } from 'bones/utils/queryBuilder/helpers/sliceTopExpression';
import {
  DraftFilter,
  DraftTopExpression,
} from 'bones/utils/queryBuilder/types';
import { getValidVariables } from 'bones/utils/queryBuilder/validation/getValidVariables';
import { validateInstanceValue } from 'bones/utils/queryBuilder/validation/validateInstanceValue';

export const validateValue = ({
  operator,
  value,
  topExpression,
  subExpressionIdx,
  expressionIdx,
  filterIdx,
  validModel,
  validVariables,
  classifyClassesByIri = {},
  copy,
}: {
  operator: DraftFilter['operator'];
  value: DraftFilter['value'];
  topExpression: DraftTopExpression;
  subExpressionIdx: number;
  expressionIdx: number;
  filterIdx: number;
  validModel?: QueryBuilderModel;
  validVariables?: Record<string, string[]>;
  classifyClassesByIri?: Record<string, Set<string>>;
  copy: QueryBuilderCopy;
}): { isValid: boolean; tooltip: string } => {
  if (value === null || value === undefined) {
    return { isValid: false, tooltip: '' };
  }

  if (typeof value === 'string') {
    if (!operator || operator.operatorType === OperatorType.OBJECT) {
      return { isValid: true, tooltip: '' };
    }

    if (operator.operatorType === OperatorType.INSTANCE) {
      return validateInstanceValue(copy)(value);
    }

    if (
      !operator.range ||
      !operator.type ||
      operator.type === DatatypeOperatorType.EXISTS ||
      operator.type === DatatypeOperatorType.DOES_NOT_EXIST
    ) {
      return { isValid: true, tooltip: '' };
    }

    const { range, type } = operator;
    switch (range) {
      case XSD_BOOLEAN_IRI: {
        const isValid = value === 'true' || value === 'false';
        return { isValid, tooltip: '' };
      }
      case XSD_INTEGER_IRI: {
        const isValid =
          !Number.isNaN(Number.parseInt(value)) && !value.includes('.');
        return { isValid, tooltip: '' };
      }
      case XSD_DECIMAL_IRI:
      case XSD_DOUBLE_IRI:
      case XSD_FLOAT_IRI: {
        const isValid = !Number.isNaN(Number.parseFloat(value));
        return { isValid, tooltip: '' };
      }
      case XSD_DATE_IRI:
      case XSD_DATE_TIME_IRI: {
        // must not be invalid (empty)
        const isValid =
          type === DatatypeOperatorType.BETWEEN
            ? Boolean(value) &&
              value
                .split(DATE_SPLIT_SYMBOL)
                .every((dateString) => isValidDate(new Date(dateString)))
            : isValidDate(new Date(value));
        return { isValid, tooltip: '' };
      }
      case XSD_TIME_IRI: {
        // must not be invalid (empty)
        const isValid =
          type === DatatypeOperatorType.BETWEEN
            ? Boolean(value) &&
              value
                .split(DATE_SPLIT_SYMBOL)
                .every((timeString) =>
                  isValidDate(getDateFromXsdTimeString(timeString))
                )
            : isValidDate(getDateFromXsdTimeString(value));
        return { isValid, tooltip: '' };
      }

      default: {
        return { isValid: true, tooltip: '' };
      }
    }
  }

  // must be in the provided model classes
  if (
    validModel &&
    value.iri !== ANY_CLASS_IRI &&
    !validModel.classesByIri[value.iri]
  ) {
    return { isValid: false, tooltip: copy.error.classModelUndefined };
  }

  // must be a valid domain or range of the provided model properties
  if (
    validModel &&
    operator &&
    operator.operatorType !== OperatorType.INSTANCE &&
    value.iri !== ANY_CLASS_IRI
  ) {
    if (
      (operator.operatorType === OperatorType.DATATYPE ||
        operator.direction === Direction.OUTBOUND) &&
      validModel.propertiesByIri[operator.iri] &&
      validModel.propertiesByIri[operator.iri].ranges.size
    ) {
      const ranges = new Set(validModel.propertiesByIri[operator.iri].ranges);
      Object.entries(classifyClassesByIri).forEach(
        ([classIri, classifyClasses]) => {
          Array.from(classifyClasses).forEach((classifyIri) => {
            if (ranges.has(classifyIri)) {
              ranges.add(classIri);
            }
          });
        }
      );
      if (!ranges.has(value.iri)) {
        return { isValid: false, tooltip: copy.error.classModelDomainOrRange };
      }
    }
    if (
      operator.direction === Direction.INBOUND &&
      validModel.propertiesByIri[operator.iri] &&
      validModel.propertiesByIri[operator.iri].domains.size
    ) {
      const domains = new Set(validModel.propertiesByIri[operator.iri].domains);
      Object.entries(classifyClassesByIri).forEach(
        ([classIri, classifyClasses]) => {
          Array.from(classifyClasses).forEach((classifyIri) => {
            if (domains.has(classifyIri)) {
              domains.add(classIri);
            }
          });
        }
      );
      if (!domains.has(value.iri)) {
        return { isValid: false, tooltip: copy.error.classModelDomainOrRange };
      }
    }
  }

  if (validVariables && operator && operator.iri !== RDF_TYPE_IRI) {
    // must be any valid variables
    const validIds = validVariables[value.iri] || [];
    const isValid = validIds.includes(value.id);
    return {
      isValid,
      tooltip: isValid ? '' : copy.error.classPreviousInstance,
    };
  }

  // can be any unused variables
  const usedVariables = getUsedVariables(
    sliceTopExpression({
      topExpression,
      subExpressionIdx,
      expressionIdx,
      filterIdx,
    })
  );
  const usedIds = usedVariables[value.iri] || [];
  if (!usedIds.includes(value.id)) {
    return { isValid: true, tooltip: '' };
  }

  const validValues = getValidVariables({
    topExpression,
    expressionIdx,
    subExpressionIdx,
    filterIdx,
  });
  const validIds = validValues[value.iri] || [];
  const isValid = validIds.includes(value.id);
  return {
    isValid,
    tooltip: isValid ? '' : copy.error.classVariableInvalid,
  };
};
